import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api, { installationMessage } from "services/device-installation-service";
import ClaimHead from "pages/onboarding/claim/ClaimHead";
import { CLAIM_STAGES, claimStageCopy } from "pages/onboarding/claim/claimTitles";
import InstallationSummary from "pages/onboarding/claim/InstallationSummary";
import { applyFollowUps } from "pages/onboarding/claim/finalizeInstallation";

// Historical summary rendered from the persisted snapshot and its follow-up
// ledger, never from today's customer or asset records.
export default function InstallationHistoryPage() {
  const { claimId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const printed = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await api.summary(claimId);
      setSummary(data);
      setOperations(data?.operations || []);
      setError("");
    } catch (failure) {
      setError(installationMessage(failure));
    }
  }, [claimId]);

  useEffect(() => {
    load();
  }, [load]);

  // "Download summary" from the claims list opens the print dialog once loaded.
  useEffect(() => {
    if (!summary || printed.current || params.get("print") !== "1") return;
    printed.current = true;
    const button = document.querySelector("[data-installation-pdf]");
    if (button) setTimeout(() => button.click(), 300);
  }, [summary, params]);

  const retry = async (failed) => {
    setRetrying(true);
    try {
      const result = await applyFollowUps({ api, claimId, operations: failed });
      if (result.summary) setSummary(result.summary);
      setOperations(result.operations);
      setError(result.error ? installationMessage(result.error) : "");
    } finally {
      setRetrying(false);
    }
  };

  const { heading } = claimStageCopy(CLAIM_STAGES.HISTORY);
  return (
    <DashboardLayout>
      <ClaimHead stage={CLAIM_STAGES.HISTORY} />
      <DashboardNavbar />
      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2} className="no-print">
              <MDTypography variant="h5" component="h1" fontWeight="medium">
                {heading}
              </MDTypography>
              <MDButton variant="outlined" color="info" onClick={() => navigate("/onboarding/device-claims")}>
                Back to device claims
              </MDButton>
            </MDBox>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!summary && !error ? (
              <CircularProgress size={28} />
            ) : (
              <InstallationSummary summary={summary} operations={operations} onRetry={retry} retrying={retrying} error={null} historical />
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
